import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
} from 'recharts';

export const BlendedMERChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance } = useBoardChartData();

    const totalRevenue = useMemo(
        () => Number(finance?.totalRevenues) || 0,
        [finance?.totalRevenues]
    );

    const totalAdSpend = useMemo(
        () => Number(finance?.metrics?.adSpend) || 0,
        [finance?.metrics?.adSpend]
    );

    const merValue = useMemo(() => {
        if (totalAdSpend === 0) return null;
        return Math.round((totalRevenue / totalAdSpend) * 100) / 100;
    }, [totalRevenue, totalAdSpend]);

    const trendData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();

        return sortedDates.map((date) => {
            const day = finance.dailyBreakdown![date];
            const revenue = Number(day.metrics?.revenue) || 0;
            const adSpend = Number(day.metrics?.adSpend) || 0;
            const mer = adSpend > 0 ? Math.round((revenue / adSpend) * 100) / 100 : 0;

            return {
                date,
                label: format(parseISO(date), 'MMM d'),
                mer,
            };
        });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(
        () => !finance || (totalRevenue === 0 && totalAdSpend === 0),
        [finance, totalRevenue, totalAdSpend]
    );

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col [container-type:inline-size]">
            <div className="flex items-center justify-center pt-3 pb-1 px-2">
                <p
                    className="font-semibold text-center"
                    style={{
                        color: CHART_COLORS.primary,
                        fontSize: 'clamp(1.5rem, 8cqi, 2.75rem)',
                    }}
                >
                    {merValue !== null ? `${merValue}x` : t('charts.notAvailable', 'N/A')}
                </p>
            </div>
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={trendData}
                        margin={{ top: 5, right: 10, bottom: 5, left: 10 }}
                    >
                        <XAxis
                            dataKey="label"
                            axisLine={false}
                            tickLine={false}
                            tick={false}
                        />
                        <YAxis hide />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                `${value}x`,
                                t('charts.mer', 'MER'),
                            ]}
                        />
                        <Line
                            type="monotone"
                            dataKey="mer"
                            stroke={CHART_COLORS.primary}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.primary }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
